import { type Routine, useStores } from '@littlebird/sdk';
import {
  formatRoutineWeekDays,
  parseRoutineRecurrenceRule,
  WEEKDAY_ONLY_CODES,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { mainLayoutStore } from '@/components/blocks/MainLayout/main-layout.store';
import { XStack, YStack } from '@/components/ui';
import { EntityIconChip } from '@/components/ui/icon-picker';
import { IconButton } from '@/components/ui/IconButton';
import { CHAT_MIN_WIDTH } from '@/features/chat/chat-row-layout.store';
import { getRoutineActions } from '@/features/routines/routine-action-state';
import { RoutineConfigureModal } from '@/features/routines/routines-create';
import { formatRoutineListSchedule } from '@/features/routines/routines-list';
import {
  routineReadState,
  useRoutineRefresh,
} from '@/features/routines/use-routine-refresh';
import { useEdgeResize } from '@/hooks/useEdgeResize';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';
import { settingsStore } from '@/stores/settings.store';
import { uiStore } from '@/stores/ui.store';
import { Routes } from '@/types/routes';
import { formatTimeFromHHmm } from '@/utils/date';
import { navigate } from '@/utils/navigation-service';

import { AgentAvatar } from './agents-avatar';
import { AgentCustomizeDialog } from './agents-customize-dialog';
import { readAgentActivity } from './agents.hooks';
import { agentsStore, type AgentEntry } from './agents.store';

const PROFILE_MIN_WIDTH = 280;
const PROFILE_MAX_WIDTH = 560;
const PROFILE_COLLAPSE_SLACK = 80;
const PANEL_EASE = [0.32, 0.72, 0, 1] as const;
const ACTION_BUTTON_PROPS = {
  variant: 'outline',
  size: 'md',
  scale: 0.9,
  strokeWidth: 1.5,
  className: 'rounded-full',
  tooltipOptions: { side: 'bottom' },
} as const;

export const AgentProfilePill = observer(({ chatId }: { chatId: string }) => {
  const stores = useStores();
  const agent = agentsStore.getByChatId(chatId);
  if (!agent) return null;
  const open = agentsStore.isProfileOpen;
  const { activity } = readAgentActivity(stores, {
    agentId: agent.agentId,
    chatId,
    introPending: !agent.description,
    active: true,
  });
  return (
    <button
      type="button"
      aria-expanded={open}
      disabled={open}
      className="no-drag group absolute left-[calc(50%-4px)] top-4 z-10 flex max-w-[60%] -translate-x-1/2 items-center rounded-full border border-border bg-surface py-1.5 pl-3 shadow-sm transition-colors duration-200 hover:enabled:border-border-strong hover:enabled:bg-surface-hover focus-ring"
      onClick={() => agentsStore.setProfileOpen(true)}
    >
      <AgentAvatar bird={agent.bird} activity={activity} className="size-7" />
      <span className="truncate pl-2.5 pr-3.5 text-sm font-medium text-foreground transition-[padding] duration-200 ease-out group-hover:group-enabled:pr-1 group-focus-visible:pr-1">
        {agent.name}
      </span>
      <span className="flex w-0 shrink-0 justify-end overflow-hidden opacity-0 transition-[width,opacity,margin] duration-200 ease-out group-hover:group-enabled:mr-2.5 group-hover:group-enabled:w-3.5 group-hover:group-enabled:opacity-100 group-focus-visible:mr-2.5 group-focus-visible:w-3.5 group-focus-visible:opacity-100">
        <Icons.chevronRight className="size-3.5 shrink-0 text-foreground-muted" />
      </span>
    </button>
  );
});

export const AgentProfilePanel = observer(({ chatId }: { chatId: string }) => {
  const agent = agentsStore.isProfileOpen
    ? agentsStore.getByChatId(chatId)
    : undefined;
  return (
    <AnimatePresence initial={false}>
      {agent && <AgentProfile key="agent-profile" agent={agent} />}
    </AnimatePresence>
  );
});

const profileMaxWidth = () => {
  const available = mainLayoutStore.contentWidth;
  if (available <= 0) return PROFILE_MAX_WIDTH;
  const room = uiStore.isNarrowViewport
    ? available
    : available - CHAT_MIN_WIDTH;
  return Math.min(
    available,
    Math.max(PROFILE_MIN_WIDTH, Math.min(PROFILE_MAX_WIDTH, room))
  );
};

const AgentProfile = observer(({ agent }: { agent: AgentEntry }) => {
  const maxWidth = profileMaxWidth();
  const width = Math.min(agentsStore.profileWidth, maxWidth);
  const { isDragging, onResizeStart } = useEdgeResize({
    axis: 'x',
    min: Math.min(PROFILE_MIN_WIDTH, maxWidth),
    max: profileMaxWidth,
    getStartSize: () => width,
    onResize: (width) => agentsStore.setProfileWidth(width),
    onResizeEnd: () => agentsStore.saveProfileWidth(),
    collapsePastMin: PROFILE_COLLAPSE_SLACK,
    onCollapse: () => agentsStore.setProfileOpen(false),
  });

  return (
    <motion.div
      aria-label={`About ${agent.name}`}
      className={cn(
        'no-drag flex shrink-0 flex-col overflow-hidden border-l border-border-subtle',
        uiStore.isNarrowViewport
          ? 'absolute inset-y-0 right-0 z-20 bg-background-subtle shadow-lg'
          : 'relative'
      )}
      initial={{ width: 0 }}
      animate={{ width }}
      exit={{ width: 0 }}
      transition={
        isDragging ? { duration: 0 } : { duration: 0.28, ease: PANEL_EASE }
      }
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize details"
        className="group absolute inset-y-0 left-0 z-10 flex w-1.5 cursor-ew-resize justify-start"
        onMouseDown={onResizeStart}
      >
        <div
          className={cn(
            'h-full w-0 bg-border-strongest group-hover:w-0.5',
            isDragging && 'w-0.5'
          )}
        />
      </div>
      <AgentProfileContent key={agent.agentId} agent={agent} width={width} />
    </motion.div>
  );
});

const AgentProfileContent = observer(
  ({ agent, width }: { agent: AgentEntry; width: number }) => {
    const [isCustomizing, setIsCustomizing] = useState(false);
    const failed = (message: string) => (error: unknown) => {
      logError(error, message, {
        context: 'AgentProfilePanel',
        metadata: { agentId: agent.agentId },
      });
      toast.error(message);
    };
    const save = (edit: Parameters<typeof agentsStore.updateAgent>[1]) =>
      agentsStore
        .updateAgent(agent.agentId, edit)
        .catch(failed('Couldn’t save your changes to this agent'));
    const pinned = agent.pinnedAt !== null;

    return (
      <YStack className="min-h-0 flex-1" style={{ width }}>
        <YStack className="shrink-0 gap-4 px-5 py-6">
          <XStack className="items-start justify-between">
            <button
              type="button"
              aria-label="Customize"
              className="group relative flex shrink-0 rounded-xl focus-ring"
              onClick={() => setIsCustomizing(true)}
            >
              <AgentAvatar bird={agent.bird} className="size-16" />
              <span className="absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full border border-border bg-surface text-foreground-muted shadow-sm transition-colors group-hover:text-foreground">
                <Icons.pencil className="size-3" />
              </span>
            </button>
            {isCustomizing && (
              <AgentCustomizeDialog
                agent={agent}
                onClose={() => setIsCustomizing(false)}
              />
            )}
            <XStack className="-mr-2 -mt-1 shrink-0 gap-2">
              <IconButton
                {...ACTION_BUTTON_PROPS}
                icon={pinned ? Icons.unpin : Icons.pin}
                tooltip={pinned ? 'Unpin' : 'Pin'}
                onClick={() =>
                  agentsStore
                    .setPinned(agent.agentId, !pinned)
                    .catch(
                      failed(
                        pinned
                          ? 'Couldn’t unpin this agent'
                          : 'Couldn’t pin this agent'
                      )
                    )
                }
              />
              <IconButton
                {...ACTION_BUTTON_PROPS}
                icon={Icons.archive}
                tooltip="Archive"
                onClick={() =>
                  agentsStore
                    .setArchived(agent.agentId, true)
                    .then(() =>
                      navigate({ to: Routes.AgentsNewTask as string })
                    )
                    .catch(failed('Couldn’t archive this agent'))
                }
              />
              <IconButton
                {...ACTION_BUTTON_PROPS}
                icon={Icons.chevronsRight}
                tooltip="Close"
                onClick={() => agentsStore.setProfileOpen(false)}
              />
            </XStack>
          </XStack>
          <YStack className="min-w-0 gap-1">
            <AgentInlineField
              label="Name"
              value={agent.name}
              placeholder="Name"
              required
              className="w-full text-base font-medium text-foreground"
              onCommit={(name) => void save({ name })}
            />
            <AgentInlineField
              label="Label"
              value={agent.label ?? ''}
              placeholder="Add a label"
              className="w-full text-sm text-foreground-muted"
              onCommit={(label) => void save({ label: label || null })}
            />
          </YStack>
        </YStack>
        <YStack className="min-h-0 flex-1 overflow-y-auto">
          <AgentProfileSection title="About">
            <AgentInlineField
              label="Description"
              value={agent.description ?? ''}
              placeholder="What is this agent for?"
              multiline
              className="w-full text-sm text-foreground-subtle"
              onCommit={(description) =>
                void save({ description: description || null })
              }
            />
          </AgentProfileSection>
          <AgentRoutines agent={agent} />
        </YStack>
      </YStack>
    );
  }
);

const AgentProfileSection = ({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: ReactNode;
}) => (
  <YStack className="gap-2 border-t border-border-subtle px-5 py-4">
    <XStack className="items-baseline gap-1.5 text-xs font-medium">
      <span className="text-foreground-subtle">{title}</span>
      {!!count && <span className="text-foreground-muted">{count}</span>}
    </XStack>
    {children}
  </YStack>
);

type AgentInlineFieldProps = {
  label: string;
  value: string;
  placeholder: string;
  className: string;
  multiline?: boolean;
  required?: boolean;
  onCommit: (value: string) => void;
};

const AgentInlineField = ({
  label,
  value,
  placeholder,
  className,
  multiline = false,
  required = false,
  onCommit,
}: AgentInlineFieldProps) => {
  const [draft, setDraft] = useState<string>();
  const cancelled = useRef(false);

  const fieldProps = {
    'aria-label': label,
    value: draft ?? value,
    placeholder,
    className: cn(
      '-mx-1.5 rounded-md bg-transparent px-1.5 outline-none placeholder:text-foreground-muted hover:bg-surface-hover focus:bg-surface-hover',
      className
    ),
    onFocus: () => setDraft(value),
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => setDraft(event.target.value),
    onBlur: () => {
      const next = (draft ?? value).trim();
      setDraft(undefined);
      if (cancelled.current) {
        cancelled.current = false;
        return;
      }
      if (next === value || (required && next.length === 0)) return;
      onCommit(next);
    },
    onKeyDown: (
      event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      if (event.key === 'Escape') {
        cancelled.current = true;
        event.currentTarget.blur();
      } else if (
        event.key === 'Enter' &&
        (!multiline || event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        event.currentTarget.blur();
      }
    },
  };

  return multiline ? (
    <textarea
      {...fieldProps}
      rows={1}
      className={cn(
        fieldProps.className,
        'resize-none py-0.5 [field-sizing:content]'
      )}
    />
  ) : (
    <input type="text" {...fieldProps} />
  );
};

const formatRoutineCadence = (recurrenceRule: string | null) => {
  const { frequency, triggerTime, weekDays } =
    parseRoutineRecurrenceRule(recurrenceRule);
  if (frequency !== 'Weekly' || !triggerTime || !weekDays?.length)
    return formatRoutineListSchedule(recurrenceRule);
  const time = formatTimeFromHHmm(triggerTime);
  if (weekDays.length === 7) return `Every day at ${time}`;
  if (
    weekDays.length === WEEKDAY_ONLY_CODES.length &&
    WEEKDAY_ONLY_CODES.every((day) => weekDays.includes(day))
  )
    return `Weekdays at ${time}`;
  return `${formatRoutineWeekDays(weekDays)} at ${time}`;
};

const AgentRoutines = observer(({ agent }: { agent: AgentEntry }) => {
  const { routines } = useStores();
  useRoutineRefresh(true, { reports: false });
  const [editing, setEditing] = useState<Routine>();
  const agentRoutines = routines.userRoutines.filter(
    (routine) =>
      routine.deliveryAgentId === agent.agentId && routine.completedAt === null
  );

  return (
    <AgentProfileSection title="Routines" count={agentRoutines.length}>
      {agentRoutines.length > 0 ? (
        <YStack className="-mx-2">
          {agentRoutines.map((routine) => (
            <AgentRoutineRow
              key={routine.id}
              routine={routine}
              onEdit={() => setEditing(routine)}
            />
          ))}
        </YStack>
      ) : (
        routineReadState.hasLoadedRoutines && (
          <span className="text-sm text-foreground-muted">
            No routines yet. Ask {agent.name} to set one up.
          </span>
        )
      )}
      {editing && (
        <RoutineConfigureModal
          routine={editing}
          open
          onClose={() => setEditing(undefined)}
        />
      )}
    </AgentProfileSection>
  );
});

type AgentRoutineRowProps = { routine: Routine; onEdit: () => void };

const AgentRoutineRow = observer(
  ({ routine, onEdit }: AgentRoutineRowProps) => {
    const paused = routine.isPaused || routine.isAdminPaused;
    const actions = getRoutineActions(routine);
    void settingsStore.timeFormat;
    return (
      <button
        type="button"
        disabled={!actions.canConfigure}
        aria-label={`Edit ${routine.title}`}
        className="flex items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-surface-hover focus-ring disabled:hover:bg-transparent"
        onClick={onEdit}
      >
        <EntityIconChip
          icon={routine.displayIcon}
          size={14}
          className={cn('size-8 rounded-lg', paused && 'opacity-50')}
        />
        <YStack className="min-w-0 flex-1">
          <span
            className={cn(
              'truncate text-sm',
              paused ? 'text-foreground-muted' : 'text-foreground'
            )}
          >
            {routine.title}
          </span>
          <span className="truncate text-xs text-foreground-muted">
            {formatRoutineCadence(routine.recurrenceRule)}
          </span>
        </YStack>
        {paused && (
          <span className="shrink-0 rounded-full bg-surface-hover px-2 py-0.5 text-xs text-foreground-muted">
            Paused
          </span>
        )}
      </button>
    );
  }
);
